import { useState } from "react";
import { galleryImages } from "../config/siteData";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useLanguage } from "../context/LanguageContext";

export const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { t } = useLanguage();

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(galleryImages[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = (e) => {
    e.stopPropagation();
    const newIndex = currentIndex === 0 ? galleryImages.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  const goToNext = (e) => {
    e.stopPropagation();
    const newIndex = currentIndex === galleryImages.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  return (
    <section
      id="galerija"
      className="py-16 md:py-24 bg-white"
      data-testid="gallery-section"
    >
      <div className="container mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-sm font-medium tracking-wide uppercase text-[#D4A373] mb-3 block">
            {t.gallery.subtitle}
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight text-[#0F172A]"
            data-testid="gallery-title"
          >
            {t.gallery.title}
          </h2>
        </div>

        {/* Gallery Grid - Bento Style */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {galleryImages.map((image, index) => (
            <button
              key={image.id}
              onClick={() => openLightbox(index)}
              className={`relative overflow-hidden rounded-xl group cursor-pointer ${
                index === 0 ? "col-span-2 row-span-2" : ""
              }`}
              style={{ aspectRatio: index === 0 ? "1" : "4/3" }}
              data-testid={`gallery-image-${index}`}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover img-zoom"
                loading="lazy"
              />
              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm font-medium">
                  {t.gallery.enlarge}
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* Lightbox Dialog */}
        <Dialog open={!!selectedImage} onOpenChange={closeLightbox}>
          <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 border-none bg-transparent shadow-none">
            <VisuallyHidden>
              <DialogTitle>{t.gallery.subtitle}</DialogTitle>
            </VisuallyHidden>
            <div
              className="relative w-full h-full flex items-center justify-center"
              data-testid="lightbox"
            >
              {/* Close Button */}
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors duration-200"
                aria-label="Close"
                data-testid="lightbox-close"
              >
                <X size={24} />
              </button>

              {/* Previous Button */}
              <button
                onClick={goToPrevious}
                className="absolute left-4 z-50 p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors duration-200"
                aria-label="Previous"
                data-testid="lightbox-prev"
              >
                <ChevronLeft size={28} />
              </button>

              {/* Image */}
              {selectedImage && (
                <img
                  src={selectedImage.url}
                  alt={selectedImage.alt}
                  className="max-w-full max-h-[85vh] object-contain rounded-lg animate-scaleIn"
                />
              )}

              {/* Next Button */}
              <button
                onClick={goToNext}
                className="absolute right-4 z-50 p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors duration-200"
                aria-label="Next"
                data-testid="lightbox-next"
              >
                <ChevronRight size={28} />
              </button>

              {/* Image Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/50 text-white text-sm">
                {currentIndex + 1} / {galleryImages.length}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};
